import { useCallback, useMemo, useRef } from "react";
import { proxy, useSnapshot } from "valtio";
import { useCommandManager } from "../../../hooks/useCommandManager";

/** Shared state */
const photoPinchingState = proxy({
	isPhotoPinching: false,
	actions: {
		startPhotoPinch() {
			photoPinchingState.isPhotoPinching = true;
		},
		endPhotoPinch() {
			photoPinchingState.isPhotoPinching = false;
		},
	},
});

/*
- The image is visualized as a square of 1x1
- The crop is visualized as a rectangle that represents the zone (zone but scaled to cropSpace)
- the Crop takes a part out of the iput image

Fill:
----
- the square of 1x1 represents the image
- Only a part of the photo visible
- Here the crop box will have the aspect ratio of the zone
┌───────1───────┐
╔═══════1═══════╗
║               ╟─ maxNormalizedCrop (= zone aspect ratio in crop space, fit in 1x1 )
║     Crop      ║  This represents a zoom of 1
║               ║
╚═══════════════╝
└───────────────┘

┌───────1───────┐
│               │   Crop: zoom x1.3
│ ╔═══════════╗ │   - Scale maxNormalizedCrop down
│ ║   Crop    ║ 1   - Set center
│ ╚═══════════╝ │
│               │
└───────────────┘

Fit:
----
- the square of 1x1 represents the image
- Enitre photo can be zoomed
┌────╔══════1══════╗────┐
│░░░░║             ║░░░░┣─ maxNormalizedCrop (= zone aspect ratio in crop space, = fill zone rect in 1x1)
│░░░░║             ║░░░░│  This represents a zoom of 1
│░░░░║    Crop     1░░░░1
│░░░░║             ║░░░░│  ░░: everything outside the 1x1 image is clipped, this will become whitespace when rendered.
│░░░░║             ║░░░░│      Whitespace is automatically centered, not possible to control yet
└────╚═════════════╝────┘

     ┌──────1──────┐
  ┌──╔══════1══════╗──┐     Crop: zoom x1.4
  │░░║             ║░░│     - Scale maxNormalizedCrop down
  │░░║    Crop     ║░░│     - Clamp to sides of 1
  │░░║             ║░░│     - Set center
  └──╚═════════════╝──┘
     └─────────────┘

     ┌──────1──────┐
     │             │        Crop: zoom x2
     │ ╔═════════╗ │        - Scale maxNormalizedCrop down
     │ ║  Crop   ║ 1        - Clamp toides of 1
     │ ╚═════════╝ │        - Set center
     │             │
     └─────────────┘
*/

/**
 * Get a default rectangle represting the zone in crop space
 * This is not a ready to use crop: size must be clamped and center must be set
 * it represents the maximum crop that can be used for pinching (zoom of 1)
 */
export function getDefaultCrop(
	photoLook: eXpress.core.PhotoLook | undefined,
	stretchMode: eXpress.core.StretchMode,
) {
	const currentCrop = photoLook?.getCrop();
	if (!photoLook || !currentCrop) return undefined;

	const imageAspectRatio = currentCrop.imageSize.turn(currentCrop.look.angle).getAspectRatio();
	const zoneSpaceToCropSpace = eXpress.math.vector(imageAspectRatio, 1);

	/**
	 * In a normalized crop, the full image is represented as 1x1, this is a deformation
	 * To make zone in same space, multiply by this factor
	 */
	const zoneRatioInCropSpace = currentCrop.zoneSize.div(zoneSpaceToCropSpace);

	/**
	 * Largest/default crop square (for not zoomed image) to use
	 * This is a rect representing the zone (stretch to compensate scaled input 1x1 zone)
	 * This rect can be scaled and clipped to get the new crop during pinching
	 */
	const defaultCrop =
		stretchMode === eXpress.core.StretchMode.Fill
			? zoneRatioInCropSpace.fit(eXpress.math.Rect.unit)
			: zoneRatioInCropSpace.fill(eXpress.math.Rect.unit); // Entire photo can be visible

	return defaultCrop.turn(defaultCrop, currentCrop.look.angle);
}

function getCropInfo(
	zoneStretchMode: eXpress.core.StretchMode,
	defaultCrop: eXpress.math.Rect,
	currentCrop: eXpress.math.Rect,
) {
	let zoomFactor = 1;
	/** Scale amount when pinch started, calculated based on the maxNormalizedCrop */
	if (zoneStretchMode === eXpress.core.StretchMode.Fit) {
		// When fitting, we are limited by the smallest side, because both sides can be clamped by 1
		zoomFactor =
			defaultCrop.toVector().getSmallestSide() / currentCrop.toVector().getSmallestSide();
	}

	if (zoneStretchMode === eXpress.core.StretchMode.Fill) {
		// When filling, we use the largest side, because one side can be  clamped by 1
		zoomFactor =
			defaultCrop.toVector().getLargestSide() / currentCrop.toVector().getLargestSide();
	}

	return {
		zoomFactor,
		center: currentCrop.center(),
	};
}

export function updateCrop(
	defaultCrop: eXpress.math.Rect,
	set:
		| {
				center?: eXpress.math.Point;
				zoomFactor?: number;
		  }
		| undefined,
) {
	if (!defaultCrop) return undefined;

	const center = set?.center ?? defaultCrop.center();
	const zoomFactor = set?.zoomFactor ?? 1;

	// Calculate new normalized crop
	const scaledMaxNormalizedCrop = defaultCrop.scale(1 / zoomFactor);

	// Clamp values for fit
	const newClampedCropWidth = Math.min(1, scaledMaxNormalizedCrop.width);
	const newClampedCropHeight = Math.min(1, scaledMaxNormalizedCrop.height);

	const newNormalizedCropRect = eXpress.math.rect(
		center.left - newClampedCropWidth / 2,
		center.top - newClampedCropHeight / 2,
		newClampedCropWidth,
		newClampedCropHeight,
	);

	// console.log(
	// 	"updateCrop",
	// 	JSON.stringify(
	// 		{
	// 			defaultCrop: defaultCrop.toText(2),
	// 			zoomFactor: Math.round(zoomFactor * 100) / 100,
	// 			scaledCrop: scaledMaxNormalizedCrop.toText(2),
	// 			newCrop: newNormalizedCropRect.toText(2),
	// 		},
	// 		undefined,
	// 		2,
	// 	),
	// );

	return newNormalizedCropRect;
}

/**
 * Logic to pinch a photo in a zone
 * This intentionally doesn't include the logic to trigger it
 * Could be based on gestures, keyboard, ...
 */
export function usePhotoPinching({
	maxReplaceDelay = 1000,
}: {
	/** When using in a transaction, we can pass Infinity to keep the command replaceable in the undo stack until transaction is closed */
	maxReplaceDelay?: number;
} = {}) {
	const commandManager = useCommandManager();
	/** An object holding information information for Zooming a photo in a zone */
	const photoPinchingRef = useRef<
		| {
				selectedZone: eXpress.core.PhotoLookZone;
				startNormalizedCrop: eXpress.math.Rect | undefined;
				startPinchDistance: number;
				startPinchOrigin: eXpress.math.Point | undefined;
				lastCommandId: string;
		  }
		| undefined
	>(undefined);

	const { isPhotoPinching } = useSnapshot(photoPinchingState);

	const start = useCallback(function startPhotoPinching(
		selectedZone: eXpress.core.PhotoLookZone,
		pinchDistance: number,
		/**
		 * Origin in normalized image coordinates
		 * Center: [0.5, 0.5]
		 * TopRight: [0, 1]
		 * Default: center of the current crop
		 */
		pinchOrigin: eXpress.math.Point | undefined,
	) {
		photoPinchingState.actions.startPhotoPinch();

		// Start with new pinching information
		photoPinchingRef.current = {
			selectedZone,
			startNormalizedCrop: selectedZone.getPhotoLook()?.normalizedCrop,
			startPinchDistance: pinchDistance,
			startPinchOrigin: pinchOrigin,
			lastCommandId: "",
		};
	}, []);

	const update = useCallback(
		function updatePhotoPinching(
			pinchDistance: number,
			/**
			 * Origin in normalized image coordinates
			 * Center: [0.5, 0.5]
			 * TopRight: [0, 1]
			 * Default: center of the current crop
			 */
			pinchOrigin: eXpress.math.Point | undefined,
		) {
			const photoPinching = photoPinchingRef.current;
			if (!photoPinching) return undefined;

			const { startNormalizedCrop, startPinchDistance, selectedZone } = photoPinching;
			if (!startNormalizedCrop || !selectedZone) return undefined;

			// Validate pinchDistance to prevent NaN values in crop calculations
			// This can happen when CTRL+scroll browser zoom is interpreted as a pinch gesture
			if (!pinchDistance || !isFinite(pinchDistance) || pinchDistance === 0) return undefined;

			const photoLook = selectedZone.getPhotoLook();
			const currentCrop = photoLook?.getCrop();
			if (!photoLook || !currentCrop) return undefined;

			const stretchMode = selectedZone.stretchMode;

			// Get Default Crop
			const defaultNormalizedCrop = getDefaultCrop(selectedZone.getPhotoLook(), stretchMode);
			if (!defaultNormalizedCrop) return undefined;

			// Get initial pinch info
			const { zoomFactor: initialZoomFactor, center: initialCenter } = getCropInfo(
				stretchMode,
				defaultNormalizedCrop,
				startNormalizedCrop,
			);

			// Get New zoom factor and center
			const newZoomFactor = initialZoomFactor * (pinchDistance / startPinchDistance);
			const newCenter = pinchOrigin ?? initialCenter;

			// Calculate new crop
			const newNormalizedCropRect = updateCrop(defaultNormalizedCrop, {
				center: newCenter,
				zoomFactor: newZoomFactor,
			});
			if (!newNormalizedCropRect) return undefined;

			const command = new eXpress.core.commands.SetCropCommand(
				photoLook,
				newNormalizedCropRect,
				undefined,
				true,
				maxReplaceDelay,
			);
			commandManager.exec(command);

			// Remember this command id so we can 'end' it
			photoPinching.lastCommandId = commandManager.getRowId();

			return newNormalizedCropRect;
		},
		[maxReplaceDelay, commandManager],
	);

	const end = useCallback(
		function endPhotoPinching() {
			if (!photoPinchingRef.current) return;
			if (photoPinchingRef.current.lastCommandId === commandManager.getRowId()) {
				commandManager.makeLastNonInteractive();
			}

			photoPinchingRef.current = undefined;
			photoPinchingState.actions.endPhotoPinch();
		},
		[commandManager],
	);

	const cancel = useCallback(
		function cancelPhotoPinching() {
			if (!photoPinchingRef.current) return;
			if (photoPinchingRef.current.lastCommandId === commandManager.getRowId()) {
				commandManager.undo(true);
			}
			photoPinchingRef.current = undefined;
			photoPinchingState.actions.endPhotoPinch();
		},
		[commandManager],
	);

	return [
		isPhotoPinching,
		useMemo(
			// Try to return a stable object
			() => ({
				start,
				update,
				end,
				cancel,
			}),
			[start, update, end, cancel],
		),
	] as const;
}
